import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, Bell, Bot, ShoppingCart, Package,
  CalendarRange, DollarSign, FlaskConical, Store, BarChart3,
  Sparkles, LogOut, Menu, X, Shield, Webhook, UserCircle, Lock,
  CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import TrialBanner from "@/components/TrialBanner";
import VersionBadge from "@/components/VersionBadge";
import OnboardingModal from "@/components/OnboardingModal";
import { hasAccess, FEATURE_REQUIREMENTS, PLAN_LABEL } from "@/lib/plans";

const NAV = [
  { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard", feature: "dashboard" },
  { to: "/app/forecast", icon: TrendingUp, label: "Previsão de demanda", feature: "forecast" },
  { to: "/app/alerts", icon: Bell, label: "Alertas", feature: "alerts" },
  { to: "/app/copilot", icon: Bot, label: "Copiloto IA", feature: "copilot" },
  { to: "/app/recommendations", icon: ShoppingCart, label: "Recomendações", feature: "recommendations" },
  { to: "/app/catalog", icon: Package, label: "Catálogo", feature: "catalog" },
  { to: "/app/seasonality", icon: CalendarRange, label: "Sazonalidade", feature: "seasonality" },
  { to: "/app/financial", icon: DollarSign, label: "Financeiro", feature: "financial" },
  { to: "/app/simulator", icon: FlaskConical, label: "Simulador", feature: "simulator" },
  { to: "/app/stores", icon: Store, label: "Lojas", feature: "stores" },
  { to: "/app/benchmarking", icon: BarChart3, label: "Benchmarking", feature: "benchmarking" },
  { to: "/app/integrations", icon: Webhook, label: "Integrações", feature: "integrations" },
  { to: "/app/profile", icon: UserCircle, label: "Perfil & Loja", feature: "profile" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavItems = ({ onNav }) => (
    <nav className="space-y-1">
      {NAV.map(({ to, icon: Icon, label, feature }) => {
        const locked = !hasAccess(user, feature);
        const required = FEATURE_REQUIREMENTS[feature];
        return (
          <NavLink
            key={to}
            to={to}
            onClick={onNav}
            data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : locked
                  ? "text-muted-foreground/60 hover:text-muted-foreground hover:bg-accent/30 border border-transparent"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50 border border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="truncate flex-1">{label}</span>
            {locked && (
              <span
                className="flex items-center gap-1 text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary"
                title={`Requer ${PLAN_LABEL[required]}`}
              >
                <Lock className="w-2.5 h-2.5" /> {PLAN_LABEL[required]}
              </span>
            )}
          </NavLink>
        );
      })}
      <NavLink
        to="/app/pricing"
        onClick={onNav}
        data-testid="nav-pricing"
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all mt-2 ${
            isActive
              ? "bg-primary/15 text-primary border border-primary/30"
              : "text-primary/80 hover:text-primary hover:bg-primary/5 border border-primary/30"
          }`
        }
      >
        <CreditCard className="w-4 h-4 shrink-0" />
        <span className="truncate">Planos & Faturamento</span>
      </NavLink>
      {user?.is_admin && (
        <Link to="/admin/dashboard" data-testid="nav-admin-link" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-all mt-3">
          <Shield className="w-4 h-4 shrink-0" />
          <span className="truncate">Painel admin</span>
        </Link>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-[#070F1B] sticky top-0 h-screen">
        <div className="p-5 border-b border-border">
          <Link to="/app/dashboard" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold tracking-tight">SmartStockAI</span>
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <NavItems />
        </div>
        <div className="p-3 border-t border-border">
          <div className="px-2 py-2 mb-2">
            <div className="text-xs text-muted-foreground">Logado como</div>
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.company}</div>
          </div>
          <Button data-testid="logout-btn" onClick={logout} variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-foreground">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
          <VersionBadge />
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 bg-[#070F1B] border-r border-border flex flex-col">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
                <span className="font-display font-bold">SmartStockAI</span>
              </div>
              <button onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3"><NavItems onNav={() => setMobileOpen(false)} /></div>
            <div className="p-3 border-t border-border">
              <Button onClick={logout} variant="ghost" size="sm" className="w-full justify-start"><LogOut className="w-4 h-4 mr-2" /> Sair</Button>
              <VersionBadge />
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <TrialBanner />
        <header className="lg:hidden border-b border-border bg-[#050B14]/70 backdrop-blur-xl px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}><Menu className="w-5 h-5" /></button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-white" /></div>
            <span className="font-display font-bold text-sm">SmartStockAI</span>
          </div>
          <div className="w-5" />
        </header>
        <main className="flex-1 p-6 lg:p-10 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
        <OnboardingModal />
      </div>
    </div>
  );
}
